/**
 * <s-menu-tile data-src="/collections/zlato?sort_by=created-descending&view=menu-tile">
 * Header mega-menu "novinka" product tile. Loads the first time its menu item is hovered or focused
 * (or when the browser is idle) and picks, from the collection's newest products: newest available
 * product with the "Novinka" badge → newest "Novinka" → newest available → newest.
 * Reads the light `view=menu-tile` response; if that isn't served, reads the regular collection page
 * sorted by newest (its cards carry the same data-tile-* attributes). Cached per visitor session.
 */
const CACHE_TTL = 15 * 60 * 1000;

function pick(rows) {
  const isNew = (r) => r.new === '1';
  const isAvailable = (r) => r.available === '1';
  return (
    rows.find((r) => isNew(r) && isAvailable(r)) ||
    rows.find(isNew) ||
    rows.find(isAvailable) ||
    rows[0] ||
    null
  );
}

function readRows(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return [...doc.querySelectorAll('[data-tile-url]')].map((el) => ({
    url: el.dataset.tileUrl,
    title: el.dataset.tileTitle,
    price: el.dataset.tilePrice,
    img: el.dataset.tileImg,
    new: el.dataset.tileNew,
    available: el.dataset.tileAvailable,
  }));
}

async function fetchRows(src) {
  try {
    const response = await fetch(src, { headers: { Accept: 'text/html' } });
    if (!response.ok) return [];
    const html = await response.text();
    return readRows(html);
  } catch {
    return [];
  }
}

class MenuTile extends HTMLElement {
  #started = false;

  connectedCallback() {
    const trigger = this.closest('s-nav-dropdown') || this;
    const load = () => this.#load();
    trigger.addEventListener('pointerenter', load, { once: true });
    trigger.addEventListener('focusin', load, { once: true });
    if ('requestIdleCallback' in window) requestIdleCallback(load, { timeout: 4000 });
    else setTimeout(load, 2500);
  }

  async #load() {
    if (this.#started || !this.dataset.src) return;
    this.#started = true;
    const key = `gv-menu-tile:${this.dataset.src}`;
    let product;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.time < CACHE_TTL) product = cached.product;
    } catch {}
    if (product === undefined) {
      let rows = await fetchRows(this.dataset.src);
      if (rows.length === 0) rows = await fetchRows(this.dataset.src.replace(/([?&])view=menu-tile&?/, '$1').replace(/[?&]$/, ''));
      product = pick(rows);
      try {
        sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), product }));
      } catch {}
    }
    if (!this.isConnected) return;
    if (product?.url) this.#render(product);
    else this.#hide();
  }

  #render(product) {
    const template = this.querySelector('template[data-tile-template]');
    if (!template) return this.#hide();
    const tile = template.content.firstElementChild.cloneNode(true);
    const slot = (name) => tile.querySelector(`[data-slot="${name}"]`) || (tile.dataset.slot === name ? tile : null);
    slot('link').href = product.url;
    slot('title').textContent = product.title || '';
    slot('price').textContent = product.price || '';
    const image = slot('image');
    if (product.img) image.src = product.img;
    else image.remove();
    this.replaceChildren(tile);
  }

  #hide() {
    this.closest('[data-menu-tile-grid]')?.classList.remove('lg:grid-cols-[minmax(0,1fr)_17rem]');
    this.remove();
  }
}

if (!customElements.get('s-menu-tile')) customElements.define('s-menu-tile', MenuTile);
